import { useAuth } from 'lib/auth';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useMessagingSocket } from 'providers/messaging';
import { motion, AnimatePresence } from 'framer-motion';
import { Reply, Copy, Pin, Forward, CheckSquare, Trash2 } from 'lucide-react';

export const MessageList = ({ messages, conversationId }) => {
  const { user } = useAuth();
  const { socket, connected } = useMessagingSocket();
  const scrollRef = useRef(null);
  const bottomRef = useRef(null);

  const [isNearBottom, setIsNearBottom] = useState(true);
  const [hoveredId, setHoveredId] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [messageReactions, setMessageReactions] = useState({});
  const [selected, setSelected] = useState([]);
  const [selectMode, setSelectMode] = useState(false);

  // scroll logic
  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    setIsNearBottom(distanceFromBottom < 150);
  };

  useEffect(() => {
    if (isNearBottom && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, conversationId, isNearBottom]);

  // listen for reactions
  useEffect(() => {
    if (!socket) return;
    const handleReaction = (data) =>
      setMessageReactions((prev) => ({
        ...prev,
        [data.messageId]: data.reactions || [],
      }));
    socket.on('message:reaction', handleReaction);
    return () => socket.off('message:reaction', handleReaction);
  }, [socket]);

  // ESC exits select mode
  useEffect(() => {
    const exitSelect = (e) => {
      if (e.key === 'Escape') {
        setSelectMode(false);
        setSelected([]);
      }
    };
    window.addEventListener('keydown', exitSelect);
    return () => window.removeEventListener('keydown', exitSelect);
  }, []);

  const handleReact = (messageId, emoji) => {
    if (!connected) return;
    socket.emit('message:react', { messageId, emoji });
  };

  const handleRightClick = (e, messageId) => {
    e.preventDefault();
    setContextMenu({ x: e.pageX, y: e.pageY, messageId });
  };

  const closeContextMenu = () => setContextMenu(null);

  const toggleSelectMode = () => {
    setSelectMode((prev) => !prev);
    setSelected([]);
  };

  const toggleMessageSelect = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleDeleteSelected = () => {
    if (!selected.length) return;
    if (confirm(`Delete ${selected.length} messages?`)) {
      console.log('Deleting messages:', selected);
      // TODO: emit socket event or call API to delete
      setSelected([]);
      setSelectMode(false);
    }
  };

  const handleForwardSelected = () => {
    if (!selected.length) return;
    alert(`Forwarding ${selected.length} messages`);
  };

  const groupReactions = (reactions = []) => {
    const counts = {};
    reactions.forEach((r) => {
      const e = r.emoji || r;
      counts[e] = (counts[e] || 0) + 1;
    });
    return Object.entries(counts);
  };

  if (!messages?.length)
    return <div className="text-center text-gray-400 mt-10">No messages yet</div>;

  const orderedMessages = messages
    ?.slice()
    ?.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

  return (
    <div className="flex flex-col h-full">
      {/* Select Mode Toolbar */}
      <AnimatePresence>
        {selectMode && (
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -20, opacity: 0 }}
            className="sticky top-0 z-20 bg-white border-b shadow-sm flex items-center justify-between px-4 py-2"
          >
            <span className="text-sm font-medium text-gray-700">
              {selected.length ? `${selected.length} selected` : 'Select messages'}
            </span>
            <div className="flex gap-3">
              <button
                onClick={handleForwardSelected}
                className="text-gray-500 hover:text-blue-600"
                title="Forward"
              >
                <Forward size={16} />
              </button>
              <button
                onClick={handleDeleteSelected}
                className="text-gray-500 hover:text-red-600"
                title="Delete"
              >
                <Trash2 size={16} />
              </button>
              <button
                onClick={toggleSelectMode}
                className="text-gray-500 hover:text-gray-700"
                title="Cancel"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat messages */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex flex-col gap-3 overflow-y-auto scroll-smooth px-2 pt-2 pb-4"
      >
        {orderedMessages.map((m, idx) => {
          const mine = m.senderId === user?._id;
          const reactions = messageReactions[m._id] || m.reactions || [];
          const grouped = groupReactions(reactions);
          const selectedMsg = selected.includes(m._id);

          return (
            <motion.div
              key={m._id || idx}
              onContextMenu={(e) => handleRightClick(e, m._id)}
              onClick={() => {
                if (selectMode) toggleMessageSelect(m._id);
              }}
              onMouseEnter={() => setHoveredId(m._id)}
              onMouseLeave={() => setHoveredId(null)}
              className={clsx(
                'flex relative transition-all duration-150',
                mine ? 'justify-end' : 'justify-start'
              )}
              animate={{
                backgroundColor: selectedMsg ? 'rgba(59,130,246,0.1)' : 'transparent',
                scale: selectedMsg ? 0.98 : 1,
              }}
              transition={{ duration: 0.15 }}
            >
              <div
                className={clsx(
                  'rounded-xl p-3 max-w-[70%] shadow-sm relative group',
                  mine
                    ? 'bg-green-100 text-gray-800 rounded-br-none'
                    : 'bg-gray-100 text-gray-700 rounded-bl-none'
                )}
              >
                <div>{m.content || '[encrypted message]'}</div>
                <div className="text-xs text-gray-500 mt-1 text-right">
                  {new Date(m.createdAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </div>

                {/* Reactions */}
                {grouped.length > 0 && (
                  <div className="flex gap-1 mt-1 justify-end flex-wrap">
                    {grouped.map(([emoji, count], i) => (
                      <span
                        key={i}
                        className="text-xs bg-white/70 rounded-full px-1.5 py-0.5 border text-gray-600"
                      >
                        {emoji} {count > 1 && <span className="text-[10px] ml-0.5">×{count}</span>}
                      </span>
                    ))}
                  </div>
                )}

                {/* Hover Reaction Bar */}
                {hoveredId === m._id && !selectMode && (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    transition={{ duration: 0.2 }}
                    className={clsx(
                      'absolute flex gap-1 -top-7 bg-white border rounded-full px-2 py-1 shadow-sm z-10',
                      mine ? 'right-0' : 'left-0'
                    )}
                  >
                    {['❤️', '😂', '👍', '🔥', '😮', '😢'].map((emoji) => (
                      <button
                        key={emoji}
                        onClick={() => handleReact(m._id, emoji)}
                        className="hover:scale-110 transition-transform text-sm"
                      >
                        {emoji}
                      </button>
                    ))}
                  </motion.div>
                )}
              </div>
            </motion.div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Context menu */}
      <AnimatePresence>
        {contextMenu && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12 }}
            style={{
              position: 'fixed',
              top: Math.min(contextMenu.y, window.innerHeight - 220),
              left: Math.min(contextMenu.x + 12, window.innerWidth - 200),
              zIndex: 9999,
              backdropFilter: 'blur(12px)',
            }}
            className="bg-white/80 rounded-xl shadow-xl border border-gray-100 py-2 w-48"
          >
            {[
              { label: 'Reply', icon: <Reply size={14} />, color: 'text-blue-600' },
              { label: 'Copy', icon: <Copy size={14} />, color: 'text-gray-600' },
              { label: 'Pin', icon: <Pin size={14} />, color: 'text-yellow-600' },
              { label: 'Forward', icon: <Forward size={14} />, color: 'text-indigo-600' },
              {
                label: 'Select',
                icon: <CheckSquare size={14} />,
                color: 'text-green-600',
                action: toggleSelectMode,
              },
              { label: 'Delete', icon: <Trash2 size={14} />, color: 'text-red-600' },
            ].map(({ label, icon, color, action }) => (
              <motion.div
                key={label}
                whileHover={{ backgroundColor: 'rgba(243,244,246,0.9)', scale: 1.03 }}
                transition={{ duration: 0.15 }}
                className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer select-none"
                onClick={() => {
                  action ? action() : console.log(`${label} clicked for`, contextMenu.messageId);
                  closeContextMenu();
                }}
              >
                <span className={clsx('p-1 rounded-md bg-gray-50', color)}>{icon}</span>
                <span className="text-gray-700">{label}</span>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
